import React, { useState, useCallback, useEffect } from "react";
import { Modal, Button, Form, Spinner } from "react-bootstrap";
import axios from "axios";
import { getURLbyEndPointV2 } from "../../../../../store/api";

const AddArbitratorForm = ({ show, onHide, disputeId, role, onSave }) => {
    const [arbitratorType, setArbitratorType] = useState("sole");
    const [arbitrators, setArbitrators] = useState([]);
    const [loadingArbitrators, setLoadingArbitrators] = useState(false);
    const [arbitratorError, setArbitratorError] = useState(null);
    const [selectedArbitrators, setSelectedArbitrators] = useState(["", "", ""]);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        // reset when opening/closing
        if (show) {
            setArbitratorType("sole");
            setSelectedArbitrators(["", "", ""]);
            setArbitratorError(null);
        }
    }, [show]);

    const fetchArbitrators = useCallback(async () => {
        if (arbitrators.length > 0) return; // avoid refetch
        setLoadingArbitrators(true);
        setArbitratorError(null);
        try {
            const res = await axios.get(getURLbyEndPointV2("getArbitratorsList"), {
                params: { disputeId, sectionIndex: 2 },
            });
            if (res?.data?.status) {
                setArbitrators(res.data.data || []);
            } else {
                setArbitratorError(res?.data?.message || "Failed to fetch arbitrators");
            }
        } catch (err) {
            setArbitratorError("Failed to fetch arbitrators");
            console.error("Failed to fetch arbitrators:", err);
        } finally {
            setLoadingArbitrators(false);
        }
    }, [disputeId, arbitrators.length]);

    const handleArbitratorChange = (index, value) => {
        setSelectedArbitrators((prev) => {
            const copy = [...prev];
            copy[index] = value;
            return copy;
        });
    };

    const getAvailableArbitrators = (index) => {
        // exclude already selected arbitrators from options
        const selected = selectedArbitrators.filter(Boolean);
        return arbitrators.filter((a) => !selected.includes(a.id) || selectedArbitrators[index] === a.id);
    };

    const handleSave = async () => {
        setSaving(true);
        try {
            const payload = {
                role,
                arbitratorType,
                arbitratorIds:
                    arbitratorType === "sole"
                        ? selectedArbitrators[0]
                            ? [selectedArbitrators[0]]
                            : []
                        : selectedArbitrators.filter(Boolean),
            };

            const res = await axios.post(getURLbyEndPointV2("addBindingArbitrator") + disputeId, payload);
            if (res?.data?.status) {
                // return the data to parent so it can update UI
                onSave && onSave(res.data.data || payload);
                onHide && onHide();
            } else {
                setArbitratorError(res?.data?.message || "Failed to save arbitrator");
            }
        } catch (err) {
            console.error("Error saving arbitrator:", err);
            setArbitratorError("Failed to save arbitrator");
        } finally {
            setSaving(false);
        }
    };

    return (
        <Modal show={show} onHide={onHide} onShow={fetchArbitrators}>
            <Modal.Header closeButton>
                <Modal.Title>Add Arbitrator ({role || "Party"})</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <div className="mb-2">
                    <Form.Check
                        type="radio"
                        label="Sole Arbitrator"
                        name="arbitratorType"
                        value="sole"
                        checked={arbitratorType === "sole"}
                        onChange={(e) => {
                            setArbitratorType(e.target.value);
                            setSelectedArbitrators(["", "", ""]);
                        }}
                    />
                    <Form.Check
                        type="radio"
                        label="Arbitral Tribunal"
                        name="arbitratorType"
                        value="tribunal"
                        checked={arbitratorType === "tribunal"}
                        onChange={(e) => {
                            setArbitratorType(e.target.value);
                            setSelectedArbitrators(["", "", ""]);
                        }}
                    />
                </div>

                {arbitrators.length > 0 ? (
                    <div>
                        {arbitratorType === "sole" ? (
                            <Form.Group className="mb-2">
                                <Form.Label>Select Sole Arbitrator</Form.Label>
                                <Form.Select
                                    value={selectedArbitrators[0]}
                                    onClick={fetchArbitrators}
                                    onChange={(e) => handleArbitratorChange(0, e.target.value)}
                                    disabled={loadingArbitrators}
                                >
                                    <option value="">{loadingArbitrators ? "Loading..." : "Select Arbitrator"}</option>
                                    {arbitrators.map((arb) => (
                                        <option key={arb.id} value={arb.id}>
                                            {arb.name}
                                        </option>
                                    ))}
                                </Form.Select>
                            </Form.Group>
                        ) : (
                            <>
                                {[0, 1, 2].map((index) => (
                                    <Form.Group key={index} className="mb-2">
                                        <Form.Label>Select Arbitrator {index + 1}</Form.Label>
                                        <Form.Select
                                            value={selectedArbitrators[index]}
                                            onClick={fetchArbitrators}
                                            onChange={(e) => handleArbitratorChange(index, e.target.value)}
                                            disabled={loadingArbitrators}
                                        >
                                            <option value="">{loadingArbitrators ? "Loading..." : "Select Arbitrator"}</option>
                                            {getAvailableArbitrators(index).map((arb) => (
                                                <option key={arb.id} value={arb.id}>
                                                    {arb.name}
                                                </option>
                                            ))}
                                        </Form.Select>
                                    </Form.Group>
                                ))}
                            </>
                        )}
                    </div>
                ) : (
                    <div className="text-center text-muted">{loadingArbitrators ? "Loading arbitrators..." : "No arbitrators available"}</div>
                )}

                {arbitratorError && <div className="text-danger mt-2">{arbitratorError}</div>}
            </Modal.Body>
            <Modal.Footer>
                <Button variant="secondary" onClick={onHide} disabled={saving}>
                    Cancel
                </Button>
                <Button variant="primary" onClick={handleSave} disabled={saving}>
                    {saving ? (
                        <>
                            <Spinner animation="border" size="sm" /> Saving...
                        </>
                    ) : (
                        "Save"
                    )}
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

export default AddArbitratorForm;
